// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/OnboardingV2Page.tsx (L184-192: the staged entrance, 150 / 700 / 1200 ms)
import { useEffect, useState } from 'react';

import { prefersReducedMotion } from '@/lib/motion';

/** TP's entrance timers, in ms after the chat opens: the opener lands, then Juno's question, then the composer rises. */
export const ENTRANCE_MS = { opener: 150, askSite: 700, dock: 1200 } as const;

/** What the entrance has drawn so far: 0 nothing, 1 the opener, 2 Juno's question, 3 the dock (the rest of the chat). */
export type EntranceStage = 0 | 1 | 2 | 3;

export interface EntranceStages {
  stage: EntranceStage;
  /** True once the dock may show (the entrance is over). */
  settled: boolean;
}

/**
 * The staged entrance of a FRESH chat, as presentation only. The transcript is derived and complete; this
 * decides when its first lines and the dock appear. Everything is drawn at once when the chat was resumed
 * (a website was already given), when `paced` is false (tests), or under reduced motion. The decision is taken
 * on mount: later changes (a restart, a new question) never replay the entrance, and nothing here is read back
 * into state, so a reload can never get stuck behind a timer.
 */
export function useEntranceStages(fresh: boolean, paced: boolean): EntranceStages {
  const [instant] = useState(() => !fresh || !paced || prefersReducedMotion());
  const [stage, setStage] = useState<EntranceStage>(instant ? 3 : 0);

  useEffect(() => {
    if (instant) return;
    const timers = [
      window.setTimeout(() => setStage(1), ENTRANCE_MS.opener),
      window.setTimeout(() => setStage(2), ENTRANCE_MS.askSite),
      window.setTimeout(() => setStage(3), ENTRANCE_MS.dock),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [instant]);

  return { stage, settled: stage === 3 };
}

/** The transcript lines the entrance holds back at `stage`: the opener until stage 1, Juno's question until stage 2. */
export function visibleAtStage<T extends { id: string }>(items: readonly T[], stage: EntranceStage): T[] {
  return items.filter((item) => (item.id === 'opener' ? stage >= 1 : item.id === 'ask-site' ? stage >= 2 : true));
}
